import {
  addExpensesBulk,
  ensureMonth,
  metaGet,
  metaSet,
  factoryReset,
  seedFactoryBudget,
} from './repo';
import { monthLabel } from '@/app/format';

const V1_KEY = 'budgetTracker.v1';

interface V1Expense {
  id: string;
  name: string;
  category: string;
  amount: number;
  notes: string;
  paid: boolean;
  paidAt: string | null;
}
interface V1Month {
  expenses: V1Expense[];
}
interface V1State {
  income: number;
  currentMonth: string;
  months: string[];
  data: Record<string, V1Month>;
  filter?: 'all' | 'paid' | 'unpaid';
  theme?: 'light' | 'dark';
  amountsHidden?: boolean;
}

/** Read v1's localStorage state. Returns null if not present or malformed. */
function readV1(): V1State | null {
  try {
    const raw = localStorage.getItem(V1_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as V1State;
    if (!parsed?.data || !parsed.currentMonth) return null;
    return parsed;
  } catch {
    return null;
  }
}

export type OnboardingChoice = 'defaults' | 'empty' | 'import';

export interface MigrationResult {
  imported: number;       // # expenses carried over
  months: number;         // # months carried over
  fromV1: boolean;
  factorySeeded: boolean;
}

/**
 * One-time migration. Idempotent.
 *  - If v1 localStorage exists and we haven't imported yet, copy it across.
 *  - If user is fresh and chose 'defaults', seed the factory budget.
 *  - If user chose 'empty', do nothing (they'll build their own).
 */
export async function migrateFromV1(choice: OnboardingChoice = 'defaults'): Promise<MigrationResult> {
  const alreadyImported = await metaGet('v1_imported_at');

  let imported = 0;
  let months = 0;
  let fromV1 = false;
  let factorySeeded = false;

  const v1 = readV1();

  if (v1 && !alreadyImported) {
    // Copy v1 → v2 store
    for (const ym of v1.months) {
      const month = v1.data[ym];
      if (!month) continue;
      await ensureMonth(ym, monthLabel(ym));
      months++;
      if (month.expenses?.length) {
        await addExpensesBulk(
          month.expenses.map(e => ({
            ym,
            name: e.name,
            category: e.category as any,
            amount: e.amount,
            notes: e.notes || '',
            source: 'imported:v1',
          })) as any,
        );
        imported += month.expenses.length;
        // Restore paid status (addExpensesBulk only inserts unpaid).
        // We do that here by directly updating — but we don't have a public
        // bulk toggle, so iterate via repo's DB after insert.
        const { getDb } = await import('./db');
        const db = await getDb();
        for (const e of month.expenses) {
          if (e.paid) {
            db.exec({
              sql: 'UPDATE expenses SET paid=1, paid_at=? WHERE id=(SELECT id FROM expenses WHERE ym=? AND name=? AND source=? LIMIT 1)',
              bind: [e.paidAt, ym, e.name, 'imported:v1'],
            });
          }
        }
      }
    }
    await metaSet('income', String(v1.income));
    await metaSet('currentMonth', v1.currentMonth);
    if (v1.theme)     await metaSet('theme', v1.theme);
    if (v1.filter)    await metaSet('filter', v1.filter);
    if (typeof v1.amountsHidden === 'boolean') await metaSet('amountsHidden', v1.amountsHidden ? '1' : '0');
    await metaSet('v1_imported_at', new Date().toISOString());
    fromV1 = true;
  }

  // Fresh user who picked 'defaults' and had no v1 data → seed factory.
  if (!fromV1 && choice === 'defaults') {
    const existing = await metaGet('currentMonth');
    if (!existing) {
      await seedFactoryBudget();
      factorySeeded = true;
    }
  }

  await metaSet('onboarded', choice);

  return { imported, months, fromV1, factorySeeded };
}

/** Wipe everything and reseed factory defaults. */
export async function fullFactoryReset(): Promise<void> {
  await factoryReset();
  await seedFactoryBudget();
}

/** Force-re-import v1 data (idempotent guard via the v1_imported_at flag is the caller's job). */
export async function reimportV1(): Promise<MigrationResult> {
  const { getDb } = await import('./db');
  const db = await getDb();
  db.exec("DELETE FROM expenses WHERE source = 'imported:v1'");
  // Reset v1_imported_at so migrateFromV1 re-runs cleanly
  db.exec("DELETE FROM meta WHERE key = 'v1_imported_at'");
  return migrateFromV1('defaults');
}
